import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription,
} from "@/components/ui/drawer";

// True below the `lg` breakpoint (matches the app's sidebar/mobile split).
export function useIsMobile(breakpoint = 1024) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width:${breakpoint - 1}px)`);
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [breakpoint]);
  return isMobile;
}

/**
 * Adaptive modal: a bottom-sheet on mobile (vaul Drawer) and a centered
 * dialog on desktop (Radix Dialog). Both use a flex column with a scrollable
 * body and shrink-0 header/footer so primary actions are never pushed
 * off-screen and content never touches the viewport edges.
 */
export function ResponsiveModal({
  open,
  onOpenChange,
  title,
  description,
  icon,
  children,
  footer,
  testid,
  contentClassName = "",
}) {
  const isMobile = useIsMobile();

  const Head = ({ TitleC, DescC, extra }) =>
    (title || description) ? (
      <div className={`shrink-0 text-left ${extra}`}>
        <div className="flex items-center gap-2.5">
          {icon}
          <TitleC className="font-serif text-h3 text-olive">{title}</TitleC>
        </div>
        {description && (
          <DescC className="text-sm text-stone leading-relaxed mt-1.5">{description}</DescC>
        )}
      </div>
    ) : null;

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent
          data-testid={testid}
          className={`bg-sand border-border flex flex-col max-h-[92vh] ${contentClassName}`}
        >
          <DrawerHeader className="p-0 px-5 pt-1 pb-1 space-y-0 text-left">
            <Head TitleC={DrawerTitle} DescC={DrawerDescription} />
          </DrawerHeader>
          <div className="px-5 py-3 flex-1 min-h-0 overflow-y-auto overscroll-contain">{children}</div>
          {footer && (
            <div className="shrink-0 p-4 pb-safe border-t border-border bg-sand">{footer}</div>
          )}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={testid}
        className={`bg-sand border-border w-[calc(100%-2rem)] max-w-lg rounded-3xl p-0 gap-0 overflow-hidden flex flex-col max-h-[90vh] ${contentClassName}`}
      >
        <DialogHeader className="p-6 pb-2 space-y-0 text-left">
          <Head TitleC={DialogTitle} DescC={DialogDescription} />
        </DialogHeader>
        <div className="px-6 py-2 flex-1 min-h-0 overflow-y-auto">{children}</div>
        {footer && <div className="shrink-0 p-4 border-t border-border">{footer}</div>}
      </DialogContent>
    </Dialog>
  );
}
